/**
 * QueryBar — type a sentence, get a query you can see and correct.
 *
 * ─── IT DRIVES THE CONTROLS, IT DOES NOT REPLACE THEM ───
 * Submitting sets the page's filters. It does not run a hidden query of its own. That is the
 * whole design: whatever the bar understood is visible in the filter row a second later, so the
 * operator can fix one chip instead of rewording a sentence and hoping. A search box that
 * silently becomes the source of truth is a search box you cannot debug from the outside.
 *
 * It is also why this survives the arrival of neural search unchanged. Swap `parseQuery` for an
 * `await search.parseQuery(text)` and this component, the chip row and the controls all keep
 * working — the interface is "a parse", not "results".
 *
 * ─── WHAT IT ADMITS TO ───
 * Two things are always shown, because both are ways a search can be confidently wrong:
 *   CORRECTIONS — "read *shire* as **shirt**". A fix the operator cannot see is a query they
 *                 did not write.
 *   UNREAD WORDS — a colour the estate does not collect, a room that is not a camera. Silence
 *                 here is the worst outcome: the filter is simply absent and the results look
 *                 like a valid answer to the question that was asked.
 */
import { useState, type FormEvent } from 'react';
import {
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { ParsedQuery } from './queryParser';
import { QUERY_EXAMPLES } from './queryParser';
import { QUESTION_BY_KEY } from './searchModel';

export interface QueryBarProps {
  /** Runs the parse and applies it to the page's filters. */
  onSubmit: (text: string) => ParsedQuery | null;
  /** The last parse, so the bar can report what it read. Null once the filters diverge from it. */
  parsed: ParsedQuery | null;
  /** Clears both the text and the parse report. */
  onClear: () => void;
}

export default function QueryBar({ onSubmit, parsed, onClear }: QueryBarProps) {
  const [text, setText] = useState('');

  const submit = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setText(trimmed);
    onSubmit(trimmed);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    submit(text);
  };

  const clear = () => {
    setText('');
    onClear();
  };

  return (
    <div className="border-b border-border bg-card px-4 py-2.5">
      <form onSubmit={handleSubmit} className="flex items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ask in words — “red shirt person visited which areas”"
            aria-label="Search in words"
            className="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-8 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none"
          />
          {text && (
            <button
              type="button"
              onClick={clear}
              aria-label="Clear the query"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        <button
          type="submit"
          disabled={!text.trim()}
          className="shrink-0 rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
        >
          Ask
        </button>
      </form>

      {/* The examples ARE the documentation. Nobody reads a help panel about a search box, and
          each of these reaches a different answer shape — which is the thing an operator has to
          find out exists at all. Shown only while the bar is empty, so they teach and then get
          out of the way. */}
      {!text && !parsed && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Try</span>
          {QUERY_EXAMPLES.map((example) => (
            <button
              key={example}
              type="button"
              onClick={() => submit(example)}
              className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {example}
            </button>
          ))}
        </div>
      )}

      {parsed && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
          <span className="flex items-center gap-1 text-muted-foreground">
            <SparklesIcon className="h-3.5 w-3.5" />
            Read as
          </span>
          <span className="rounded-full bg-brand-light px-2 py-0.5 font-medium text-foreground">
            {QUESTION_BY_KEY[parsed.question]?.label || parsed.question}
          </span>

          {/* Every spelling fix, named both ways. */}
          {parsed.corrections.map(([typed, readAs]) => (
            <span key={`${typed}-${readAs}`} className="text-muted-foreground">
              <span className="line-through">{typed}</span>
              {' → '}
              <strong className="font-medium text-foreground">{readAs}</strong>
            </span>
          ))}

          {parsed.notes.map((note) => (
            <span key={note} className="text-muted-foreground">{note}</span>
          ))}

          {/* Amber, not grey: this is the case where the query is narrower than the sentence, and
              the results below are a real answer to a DIFFERENT question. */}
          {parsed.unparsed.length > 0 && (
            <span className="flex items-center gap-1 text-amber-500">
              <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" />
              Could not read {parsed.unparsed.map((w) => `“${w}”`).join(', ')} — not a filter or a
              place on this node, so it was left out
            </span>
          )}

          <span className="ml-auto text-muted-foreground">
            Adjust anything below — the controls are the query
          </span>
        </div>
      )}
    </div>
  );
}
